import { createFileRoute } from "@tanstack/react-router";
import {
  IndustryMarketingPage,
  industryMarketingHead,
  type IndustryFAQ,
} from "@/components/IndustryMarketingPage";

const FAQS: IndustryFAQ[] = [
  {
    q: "How do tutors and coaches get more clients in Colorado?",
    a: "Local SEO + service-specific pages. 'Math tutor', 'SAT prep', 'ACT prep', 'reading specialist', and 'executive function coach' are all distinct searches. Tutors with one generic 'tutoring' page are invisible. Build one page per subject + grade band and conversions multiply.",
  },
  {
    q: "Is Google Ads worth it for tutoring?",
    a: "Yes, especially August–October for back-to-school and January–March for SAT/ACT season. Outside those windows, lean on SEO and parent referrals. Year-round broad ads burn budget.",
  },
  {
    q: "What about online vs in-person tutoring?",
    a: "Build separate pages for each. 'Online math tutor' and 'in-home tutor near me' are different searches with different conversion logic. Both should live on your site.",
  },
  {
    q: "Does this work for life coaches and executive coaches too?",
    a: "Yes — coaching has higher per-client value than tutoring, which makes SEO especially profitable. Niche pages ('career coach for engineers', 'ADHD executive function coach') close at 5–10x the rate of generic coaching pages.",
  },
  {
    q: "Do you build sites for solo tutors or learning centers?",
    a: "Both. Solo tutors get a personal-brand site with booking and parent testimonials. Centers get multi-tutor sites with subject pages, intake forms, and staff bios.",
  },
];

export const Route = createFileRoute("/tutor-marketing-colorado")({
  head: () =>
    industryMarketingHead("Tutor & Coach", "tutor or coach", "tutor-marketing-colorado", FAQS),
  component: () => (
    <IndustryMarketingPage
      industry="Tutor & Coach"
      industryLower="tutor or coach"
      slug="tutor-marketing-colorado"
      subheadline="We help Colorado tutors, test-prep specialists, and coaches build a steady pipeline of parent inquiries and high-value coaching clients."
      painPoints={[
        "Your roster is full every August and empty every June — no off-season pipeline",
        "Parents search 'SAT prep near me' and find national chains instead of you",
        "Coaching clients can't find your niche — your site reads like a generic life coach",
      ]}
      serviceDescriptions={{
        seo: "Rank for subject-specific tutoring, test prep, and niche coaching searches across every Colorado market.",
        googleMaps: "Own 'tutor near me' in the Map Pack so back-to-school parents call you first.",
        webDesign: "Per-subject and per-coaching-niche pages with online booking, parent testimonials, and trust signals.",
        ppc: "Seasonal Google Ads (Aug–Oct, Jan–Mar) that capture back-to-school and test-prep surges.",
        geoAi: "Get cited when parents ask ChatGPT 'best math tutor in Colorado Springs' or 'ADHD coach near me'.",
        socialMedia: "Parent-trust content and student-success stories that drive referrals year-round.",
      }}
      testimonial={{
        quote:
          "Our SAT prep page filled the fall completely. We've never had a pipeline this strong without paid ads.",
        role: "Test Prep Tutor",
        city: "Colorado Springs",
      }}
      faqs={FAQS}
      cityHubLinks={[
        { to: "/colorado-springs", label: "Tutor & coach marketing in Colorado Springs", description: "Build a steady parent pipeline across Academy District 20 and northern Springs." },
        { to: "/denver-digital-marketing", label: "Tutor & coach marketing in Denver", description: "Compete in Denver's saturated test-prep and coaching market." },
        { to: "/boulder-digital-marketing", label: "Tutor & coach marketing in Boulder", description: "High-engagement Boulder parents and high-net-worth coaching prospects." },
      ]}
    />
  ),
});
